'use client';
import React from 'react';
import Link from 'next/link';

const FEATURES = [
  {
    id: 'f1',
    title: 'Cow Records',
    desc: 'Track every cow and her history.',
  },
  {
    id: 'f2',
    title: 'Calf Tracking',
    desc: 'Record births, weights, and sales.',
  },
  {
    id: 'f3',
    title: 'Breeding Logs',
    desc: 'Track bull turnout and AI breeding.',
  },
  {
    id: 'f4',
    title: 'Health Records',
    desc: 'Log treatments, vaccinations, and worming.',
  },
  {
    id: 'f5',
    title: 'Mobile Friendly',
    desc: 'Use easily from your phone while working cattle.',
  },
  {
    id: 'f6',
    title: 'Ranch Notebook',
    desc: 'Every herd event in one chronological journal.',
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <span className="font-bold text-lg text-gray-900">CattleBooks</span>
            <span className="text-xs text-gray-500 ml-2 hidden sm:inline">Simple cattle records</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/sign-up-login-screen"
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Sign In
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="px-4 py-2 bg-green-800 text-white text-sm font-semibold rounded-lg hover:bg-green-900 transition-colors"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </header>
      {/* Hero */}
      <section className="px-6 py-16 sm:py-24 max-w-4xl mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
          Simple cattle records for cow-calf ranchers
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          Track cows, calves, breeding, treatments, and herd history without complicated software.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            href="/sign-up-login-screen"
            className="px-8 py-4 bg-green-800 text-white text-base font-semibold rounded-xl hover:bg-green-900 transition-colors"
          >
            Start Free Trial
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="px-8 py-4 border border-gray-300 text-gray-700 text-base font-semibold rounded-xl hover:bg-gray-50 transition-colors"
          >
            Sign In
          </Link>
        </div>
        <p className="text-sm text-gray-500 mt-4">14-day free trial. No credit card required.</p>
      </section>
      {/* Features */}
      <section className="px-6 py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">
            Everything you need to run your herd records
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES?.map((f) => (
              <div key={f?.id} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{f?.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Pricing */}
      <section className="px-6 py-16 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Simple pricing</h2>
        <div className="max-w-sm mx-auto border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
          <p className="font-bold text-xl text-gray-900 mb-2">CattleBooks</p>
          <p className="text-5xl font-bold text-gray-900 mb-1">$5</p>
          <p className="text-gray-500 mb-6">per month</p>
          <ul className="text-sm text-gray-700 space-y-2 mb-8 text-left">
            <li>Unlimited cattle</li>
            <li>Unlimited herd records</li>
            <li>Mobile friendly</li>
            <li>All features included</li>
          </ul>
          <Link
            href="/sign-up-login-screen"
            className="block w-full py-4 bg-green-800 text-white font-semibold rounded-xl hover:bg-green-900 transition-colors"
          >
            Start Free Trial
          </Link>
          <p className="text-xs text-gray-500 mt-3">14-day free trial. Cancel anytime.</p>
        </div>
      </section>
      {/* Footer */}
      <footer className="border-t border-gray-100 px-6 py-8 text-center">
        <p className="font-bold text-gray-900 mb-1">CattleBooks</p>
        <p className="text-sm text-gray-500">Simple cattle records for cow-calf ranchers</p>
        <div className="flex justify-center gap-4 mt-4 text-sm text-gray-500">
          <Link href="/sign-up-login-screen" className="hover:text-gray-700">Sign In</Link>
          <Link href="/install-app" className="hover:text-gray-700">Install App</Link>
        </div>
      </footer>
    </div>
  );
}
